// lib
import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { CUSTOM_QUESTION_REG } from '@aytm/app/stats/page/components/lib/constants'
import { hasComments } from '@aytm/app/stats/page/components/lib/utils'
import { calcVerbatimDownloadParams } from '@aytm/app/stats/page/question-views/components/controls/lib/export-params'

// components
import ExportDropdown from '@aytm/app/stats/components/export-dropdown/ExportDropdown'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

// styles
import style from '@aytm/app/stats/page/question-views/components/controls/styles/Controls.module.scss'
import { isWcChartMode } from '@aytm/app/stats/page/question-views/components/lib/chart-modes'
import { NO_PRINT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const Controls = ({
  model,
  features,
  disabled,
  isWCType,
  isVerbatimType,
  isSurveyGuest,
  onChartModeChange,
  onExport
}) => {
  const { id, type, items, chartMode } = model

  const exportOptions = useMemo(() => {
    const options = []

    const allowed = ['oe', 'oex', 'cn'].includes(type) || null === items || hasComments(items)
    if (allowed && !features.oe_tags_management.disabled) {
      options.push({ key: 'csv-verbatim', text: 'Download all verbatim (CSV)' })
      if (!CUSTOM_QUESTION_REG.test(String(id).split('-')[0])) {
        options.push({ key: 'csv-verbatim-codes', text: 'Download all verbatim with codes (CSV) ' })
      }
    }

    return options
  }, [id, type, items, features.oe_tags_management.disabled])

  const onExportClick = (key, callback) => {
    const [fileType, exportType, exportExtra] = key.split('-')

    const download = calcVerbatimDownloadParams({ key: model.key, fileType, exportType, exportExtra })
    if (!download) return callback?.()

    onExport(download.fileType, download.exportType, download.exportOptions, callback)
  }

  // the dropdown draws nothing without options, so an offered export is not yet a control
  const canExport = isVerbatimType && !isSurveyGuest && 0 < exportOptions.length

  // the strip reserves space of its own, so it must not be drawn when it would hold nothing
  if (disabled || !(isWCType || canExport)) return null

  return (
    <div className={`${style.component} ${NO_PRINT}`}>
      {isWCType ? (
        <div className="p-right">
          <span className={style.toggle} {...pressable(onChartModeChange)}>
            {isWcChartMode(chartMode) ? 'switch to chart' : 'switch to wordcloud'}
          </span>
          {canExport ? <ExportDropdown options={exportOptions} onExport={onExportClick} /> : null}
        </div>
      ) : (
        <div className="p-right">
          <ExportDropdown options={exportOptions} onExport={onExportClick} />
        </div>
      )}
    </div>
  )
}

Controls.propTypes = {
  model: PropTypes.object,
  features: PropTypes.object,
  disabled: PropTypes.bool,
  isWCType: PropTypes.bool,
  isVerbatimType: PropTypes.bool,
  isSurveyGuest: PropTypes.bool,
  onChartModeChange: PropTypes.func,
  onExport: PropTypes.func
}

Controls.displayName = 'Controls'

export default Controls
